import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SystemService } from "@/lib/api_client";
import { getAuthHeaders, getApiBaseUrl } from "@/lib/api";

// ── Stats ────────────────────────────────────────────────

export const useSystemStats = () =>
  useQuery({
    queryKey: ["system", "stats"],
    queryFn: async () => {
      const res = await SystemService.getApiSystemStats({ headers: getAuthHeaders() });
      if (!res.data) throw new Error("No data returned");
      return res.data;
    },
    refetchInterval: 30_000,
  });

export const useJobsByWorker = () =>
  useQuery({
    queryKey: ["system", "jobs-by-worker"],
    queryFn: async () => {
      const res = await SystemService.getApiSystemJobsByWorker({ headers: getAuthHeaders() });
      return res.data ?? [];
    },
    refetchInterval: 30_000,
  });

export const useThroughput = () =>
  useQuery({
    queryKey: ["system", "throughput"],
    queryFn: async () => {
      const res = await SystemService.getApiSystemThroughput({ headers: getAuthHeaders() });
      return res.data ?? [];
    },
    refetchInterval: 30_000,
  });

// ── Jobs ────────────────────────────────────────────────

export const useSystemJobs = (filters: {
  limit?: number;
  offset?: number;
  status?: string;
  worker?: string;
}) =>
  useQuery({
    queryKey: ["system", "jobs", filters],
    queryFn: async () => {
      const res = await SystemService.getApiSystemJobs({
        headers: getAuthHeaders(),
        query: {
          limit: String(filters.limit ?? 50),
          offset: String(filters.offset ?? 0),
          status: filters.status,
          worker: filters.worker,
        },
      });
      return (res.data ?? []) as any[];
    },
  });

export const useSystemJobDetail = (id: string) =>
  useQuery({
    queryKey: ["system", "job", id],
    queryFn: async () => {
      const res = await SystemService.getApiSystemJobsById({ path: { id }, headers: getAuthHeaders() });
      return res.data!;
    },
    enabled: !!id,
  });

export const useStuckJobs = () =>
  useQuery({
    queryKey: ["system", "jobs", "stuck"],
    queryFn: async () => {
      const res = await SystemService.getApiSystemJobsStuck({ headers: getAuthHeaders() });
      return res.data!;
    },
  });

// ── Job mutations ────────────────────────────────────────

export const useRetryJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await SystemService.postApiSystemJobsByIdRetry({ path: { id }, headers: getAuthHeaders() });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system", "jobs"] });
      qc.invalidateQueries({ queryKey: ["system", "stats"] });
    },
  });
};

export const useRequeueJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await SystemService.postApiSystemJobsByIdRequeue({ path: { id }, headers: getAuthHeaders() });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system", "jobs"] });
      qc.invalidateQueries({ queryKey: ["system", "stats"] });
    },
  });
};

export const useCancelJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await SystemService.postApiSystemJobsByIdCancel({ path: { id }, headers: getAuthHeaders() });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system", "jobs"] });
      qc.invalidateQueries({ queryKey: ["system", "stats"] });
    },
  });
};

export const useDeleteJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await SystemService.deleteApiSystemJobsById({ path: { id }, headers: getAuthHeaders() });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system", "jobs"] });
      qc.invalidateQueries({ queryKey: ["system", "stats"] });
    },
  });
};

export const useRecoverStuck = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await SystemService.postApiSystemJobsRecoverStuck({ headers: getAuthHeaders() });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system", "jobs"] });
      qc.invalidateQueries({ queryKey: ["system", "stats"] });
    },
  });
};

export const useCleanupJobs = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (opts: { daysToKeep?: number; statuses?: string[] }) => {
      const res = await SystemService.postApiSystemJobsCleanup({
        body: opts,
        headers: getAuthHeaders(),
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system", "jobs"] });
      qc.invalidateQueries({ queryKey: ["system", "stats"] });
    },
  });
};

export const useScheduleJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (opts: { worker: string; args?: Record<string, unknown>; scheduledAt?: string; maxAttempts?: number }) => {
      const res = await SystemService.postApiSystemJobs({
        body: opts,
        headers: getAuthHeaders(),
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system", "jobs"] });
      qc.invalidateQueries({ queryKey: ["system", "stats"] });
    },
  });
};

// ── Logs ────────────────────────────────────────────────

export const useSystemLogs = (filters: {
  limit?: number;
  offset?: number;
  level?: string;
  context?: string;
  jobId?: string;
  search?: string;
  requestId?: string;
}) =>
  useQuery({
    queryKey: ["system", "logs", filters],
    queryFn: async () => {
      const res = await SystemService.getApiSystemLogs({
        headers: getAuthHeaders(),
        query: {
          limit: String(filters.limit ?? 100),
          offset: String(filters.offset ?? 0),
          level: filters.level,
          context: filters.context,
          jobId: filters.jobId,
          search: filters.search,
          requestId: filters.requestId,
        },
      });
      return (res.data ?? []) as any[];
    },
  });

export const useLogStats = () =>
  useQuery({
    queryKey: ["system", "logs", "stats"],
    queryFn: async () => {
      const res = await SystemService.getApiSystemLogsStats({ headers: getAuthHeaders() });
      if (!res.data) throw new Error("No data returned");
      return res.data;
    },
  });

export const useCleanupLogs = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (daysToKeep?: number) => {
      const res = await SystemService.postApiSystemLogsCleanup({
        body: { daysToKeep },
        headers: getAuthHeaders(),
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system", "logs"] });
    },
  });
};

// ── Cron ────────────────────────────────────────────────

export const useCronJobs = () =>
  useQuery({
    queryKey: ["system", "cron-jobs"],
    queryFn: async () => {
      const res = await SystemService.getApiSystemCronJobs({ headers: getAuthHeaders() });
      return (res.data ?? []) as any[];
    },
  });

export const useTriggerCron = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ worker, args }: { worker: string; args?: Record<string, unknown> }) => {
      const res = await SystemService.postApiSystemCronJobsByWorkerTrigger({
        path: { worker },
        body: { args },
        headers: getAuthHeaders(),
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system", "jobs"] });
    },
  });
};

// ── Events ────────────────────────────────────────────────

export const useEventStreams = () =>
  useQuery({
    queryKey: ["system", "event-streams"],
    queryFn: async () => {
      const res = await SystemService.getApiSystemEventsStreams({ headers: getAuthHeaders() });
      return (res.data ?? []) as string[];
    },
    staleTime: 5 * 60_000,
  });

export const useEvents = (filters: {
  stream?: string;
  streamId?: string;
  type?: string;
  limit?: number;
  offset?: number;
}) =>
  useQuery({
    queryKey: ["system", "events", filters],
    queryFn: async () => {
      const res = await SystemService.getApiSystemEvents({
        headers: getAuthHeaders(),
        query: {
          stream: filters.stream,
          streamId: filters.streamId,
          type: filters.type,
          limit: String(filters.limit ?? 50),
          offset: String(filters.offset ?? 0),
        },
      });
      return (res.data ?? []) as any[];
    },
    enabled: !!(filters.stream || filters.streamId),
  });

// ── Health / VM stats ────────────────────────────────────

export interface DiskStats {
  mount: string;
  totalMb: number;
  usedMb: number;
  usagePercent: number;
}

export interface SwapStats {
  totalMb: number;
  usedMb: number;
  usagePercent: number;
}

export interface NodeStats {
  nodeId: string;
  startedAt: string;
  lastHeartbeatAt: string;
  process: {
    uptimeSeconds: number;
    memoryMb: { rss: number; heapUsed: number; heapTotal: number; external: number };
    cpuUsage: { user: number; system: number };
    system: {
      totalMemoryMb: number;
      freeMemoryMb: number;
      memoryUsagePercent: number;
      cpuCount: number;
      loadAvg: [number, number, number];
      platform: string;
      disk?: DiskStats;
      swap?: SwapStats;
    };
  };
}

/** One VM reporting through its own cron (today: the DB VM). */
export interface HostStats {
  host: string;
  reportedAt: string;
  stats: {
    disks: DiskStats[];
    memory: { totalMb: number; availableMb: number; usagePercent: number };
    swap: SwapStats;
    loadAvg: [number, number, number];
    walMb: number;
    archiver: {
      archivedCount: number;
      failedCount: number;
      lastArchivedAt: string | null;
      lastFailedAt: string | null;
    };
    backup: {
      status: string | null;
      lastCompletedAt: string | null;
      type: string | null;
      label: string | null;
    };
  };
}

export interface PgStats {
  version: string;
  uptimeSeconds: number;
  activeConnections: number;
  idleConnections: number;
  maxConnections: number;
  connectionUsagePercent: number;
  databaseSizeMb: number;
  cacheHitRatio: number;
  deadlocks: number;
  transactionsCommitted: number;
  transactionsRolledBack: number;
  tempFilesMb: number;
  slowQueries: number;
  sharedBuffersMb: number;
  workMemMb: number;
}

export interface HealthStats {
  nodes: NodeStats[];
  hosts: HostStats[];
  pg: PgStats;
}

export const useHealthStats = () =>
  useQuery({
    queryKey: ["system", "health"],
    queryFn: async () => {
      const res = await fetch(`${getApiBaseUrl()}/api/system/health`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch health stats");
      return (await res.json()) as HealthStats;
    },
    refetchInterval: 10_000,
  });

// ── Feilsignaturer (log triage) ──────────────────────────

export type LogRange = "1h" | "24h" | "7d" | "30d";
export type LogBucket = "action" | "auth" | "noise";

export interface LogSignature {
  signature: string;
  bucket: LogBucket;
  status: number | null;
  count: number;
  users: number;
  sparkline: number[];
  firstSeen: string;
  lastSeen: string;
  isNew: boolean;
  isRising: boolean;
  mutedUntil: string | null;
  resolvedAt: string | null;
  note: string | null;
  context: string | null;
  jobId: number | null;
}

export interface LogSignatureList {
  signatures: LogSignature[];
  counts: { action: number; auth: number; noise: number; all: number };
  events: { action: number; auth: number; noise: number };
  newCounts: { action: number; auth: number; noise: number };
  topAuth: string | null;
  infoDebugEstimate: number;
  rangeFrom: string;
}

export interface LogSignatureDetail {
  signature: string;
  bucket: LogBucket;
  status: number | null;
  diagnosis: string;
  count: number;
  users: number;
  firstSeen: string | null;
  histogram: number[];
  events: {
    createdAt: string;
    authUserId: string | null;
    jobId: number | null;
    requestId: string | null;
    message: string;
  }[];
  payload: string | null;
  routes: { path: string; count: number }[];
}

// Polled so the list stays live during triage. The UI merges counts without
// moving the selected row, so a refetch never yanks the panel out from under you.
export const useLogSignatures = (filters: {
  range?: LogRange;
  bucket?: LogBucket | "all";
  q?: string;
  includeResolved?: boolean;
}) =>
  useQuery({
    queryKey: ["system", "log-signatures", filters],
    queryFn: async () => {
      const res = await SystemService.getApiSystemLogsSignatures({
        headers: getAuthHeaders(),
        query: {
          range: filters.range ?? "24h",
          bucket: filters.bucket ?? "action",
          q: filters.q || undefined,
          includeResolved: filters.includeResolved ? "true" : undefined,
        },
      });
      if (!res.data) throw new Error("No data returned");
      return res.data as unknown as LogSignatureList;
    },
    refetchInterval: 10_000,
    placeholderData: (prev) => prev,
  });

export const useLogSignature = (signature: string | null, range: LogRange = "24h") =>
  useQuery({
    queryKey: ["system", "log-signature", signature, range],
    queryFn: async () => {
      const res = await SystemService.getApiSystemLogsSignature({
        headers: getAuthHeaders(),
        query: { signature: signature!, range },
      });
      if (!res.data) throw new Error("No data returned");
      return res.data as unknown as LogSignatureDetail;
    },
    enabled: !!signature,
  });

function invalidateSignatures(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["system", "log-signatures"] });
  qc.invalidateQueries({ queryKey: ["system", "log-signature"] });
}

export const useResolveSignature = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { signature: string; note?: string }) => {
      const res = await SystemService.postApiSystemLogsSignatureResolve({
        body: vars,
        headers: getAuthHeaders(),
      });
      // The generated client returns HTTP failures in res.error without
      // throwing, so without this a 500 still toasts "Markert som løst".
      if (res.error || !res.data) throw new Error("Kunne ikke markere som løst");
      return res.data;
    },
    onSuccess: () => invalidateSignatures(qc),
  });
};

export const useMuteSignature = () => {
  const qc = useQueryClient();
  return useMutation({
    // days: 0 unmutes, so one hook covers the toggle in both directions.
    mutationFn: async (vars: { signature: string; days: number }) => {
      const res = await SystemService.postApiSystemLogsSignatureMute({
        body: vars,
        headers: getAuthHeaders(),
      });
      if (res.error || !res.data) throw new Error("Kunne ikke endre dempingen");
      return res.data;
    },
    onSuccess: () => invalidateSignatures(qc),
  });
};

export interface ErrorExport {
  prompt: string;
  signatureCount: number;
  charCount: number;
  truncated: boolean;
  droppedSignatures: number;
}

// A mutation rather than a query: the report is built on demand when the
// developer asks for it, not polled alongside the list.
export const useErrorExportPrompt = () =>
  useMutation({
    mutationFn: async (vars: {
      range?: LogRange;
      bucket?: LogBucket | "all";
      limit?: number;
    }) => {
      const res = await SystemService.getApiSystemLogsExportPrompt({
        headers: getAuthHeaders(),
        query: {
          range: vars.range ?? "24h",
          bucket: vars.bucket ?? "action",
          limit: vars.limit ? String(vars.limit) : undefined,
        },
      });
      if (!res.data) throw new Error("No data returned");
      return res.data as unknown as ErrorExport;
    },
  });

// ── Endepunkter (request telemetry) ──────────────────────

export interface RouteStat {
  method: string;
  route: string;
  count: number;
  status4xx: number;
  status5xx: number;
  /** Share of requests that ended in 5xx, percent with one decimal. */
  errorRate: number;
  avgMs: number;
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
}

export interface Telemetry {
  totals: Omit<RouteStat, "method" | "route">;
  routes: RouteStat[];
  series: {
    bucket: string;
    count: number;
    status4xx: number;
    status5xx: number;
    p95Ms: number;
  }[];
}

export const useTelemetry = (range: LogRange) =>
  useQuery({
    queryKey: ["system", "telemetry", range],
    queryFn: async () => {
      const res = await SystemService.getApiSystemTelemetry({
        headers: getAuthHeaders(),
        query: { range },
      });
      if (!res.data) throw new Error("No data returned");
      return res.data as unknown as Telemetry;
    },
    // Buckets are written once a minute, so polling faster shows nothing new —
    // and neither does refetching on every tab return or range toggle.
    refetchInterval: 60_000,
    staleTime: 60_000,
  });
